import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaNonOperationAffidavitPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Non-Operation Affidavit</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Affidavit of Non-Operation (VP 018)</h1>
        <p className="text-obsidian-300">How to avoid registration late fees when your vehicle is not being driven on public roads.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is a Non-Operation Affidavit?</h2>
          <p>The <strong>Affidavit of Non-Operation (Form VP 018)</strong> is a Nevada DMV document that certifies your vehicle has not been operated on public highways during a specific period. Filing this form before your registration expires allows you to avoid late registration fees and penalties while the vehicle is not in use.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Key Requirements</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <ul className="space-y-3">
              {[
                'Must be filed BEFORE the current registration expires — not after',
                'The vehicle must not be operated on any public road or highway',
                'The vehicle must be stored on private property',
                'You must still maintain the vehicle in a safe condition',
                'The affidavit covers one registration period',
                'You cannot drive the vehicle until you renew the registration',
              ].map(item => (
                <li key={item} className="flex items-start space-x-3 text-sm text-amber-800">
                  <i className="ri-information-line text-amber-600 flex-shrink-0 mt-0.5"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">How to File</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Obtain Form VP 018', desc: 'Download from dmv.nv.gov or pick up at any Nevada DMV office.' },
              { step: '2', title: 'Complete the Form', desc: 'Fill in your vehicle information, registration details, and the period of non-operation.' },
              { step: '3', title: 'Submit Before Expiration', desc: 'Submit the completed form to the DMV before your current registration expires. Can be submitted in person or by mail.' },
              { step: '4', title: 'Keep Your Copy', desc: 'Retain a copy of the filed affidavit as proof of non-operation.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </li>
            ))}
          </ol>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When You&apos;re Ready to Drive Again</h2>
          <p className="text-sm">When you want to put the vehicle back on the road, you must complete a full registration renewal, including passing any required emissions tests and paying all applicable fees. You cannot drive the vehicle until the registration is current.</p>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help Filing?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can assist with non-operation affidavits and all other DMV paperwork.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaNonOperationAffidavitPage;
